// Virtualised asset table: sorting, header filter menus (right-click), multi-select,
// resizable columns. Mirrors views/asset_table_widget.py + custom_filter_header.py.
import { EMPTY_CELL_TEXT, escapeHtml, formatByteSize } from "./util.js";

const MAX_COLUMN_WIDTH = 360;
const CELL_TEXT_PADDING = 16;
const OVERSCAN = 8;

export const COLUMNS = [
  { id: "name", label: "Name", width: 220 },
  { id: "type", label: "Type", width: 90, filter: "set" },
  { id: "path_id", label: "PathID", width: 170 },
  { id: "container", label: "Container", width: 240 },
  { id: "size", label: "Size", width: 80 },
  { id: "source", label: "SourceFile", width: 160, filter: "set" },
];

/** Header width besides the label: padding 8 + sort arrow 8 + filter button 18 + gaps 12 + border. */
const HEADER_CHROME = 48;

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

function cellText(asset, col) {
  switch (col) {
    case "name":
      if (!asset.name) return asset.changed ? `${EMPTY_CELL_TEXT} *` : EMPTY_CELL_TEXT;
      return asset.changed ? `${asset.name} *` : asset.name;
    case "size":
      return formatByteSize(asset.size);
    case "container":
      return asset.container || EMPTY_CELL_TEXT;
    case "source":
      return asset.source || EMPTY_CELL_TEXT;
    default:
      return String(asset[col] ?? "") || EMPTY_CELL_TEXT;
  }
}

function isPlaceholder(asset, col) {
  if (col === "name") return !asset.name;
  if (col === "container") return !asset.container;
  if (col === "source") return !asset.source;
  if (col === "type") return !asset.type;
  if (col === "path_id") return !asset.path_id;
  return false;
}

function compare(a, b, col) {
  if (col === "size") return (a.size || 0) - (b.size || 0);
  if (col === "path_id") {
    const x = BigInt(a.path_id || 0);
    const y = BigInt(b.path_id || 0);
    return x < y ? -1 : x > y ? 1 : 0;
  }
  return collator.compare(String(a[col] ?? ""), String(b[col] ?? ""));
}

export class AssetTable {
  constructor(root, { onSelectionChange, onActivate, onLongPress, onFiltersChanged } = {}) {
    this.root = root;
    this.onFiltersChanged = onFiltersChanged || (() => {});
    this.onSelectionChange = onSelectionChange || (() => {});
    this.onActivate = onActivate || (() => {});
    this.onLongPress = onLongPress || (() => {});
    this.assets = [];
    this.view = [];
    this.selected = new Set();
    this._menuSelection = null; // selection when a filter menu opened (see _recompute)
    this.anchor = -1;
    this.cursor = -1;
    this.sort = { col: "name", dir: 1 };
    this.filters = new Map(); // col -> {kind:'set', values:Set, all:[]} | {kind:'text', text, matchCase}
    this.uniques = new Map();
    this.widths = COLUMNS.map((c) => c.width);
    this.selectMode = false;
    this.compact = false; // phone layout: two-line rows, no column header
    this.quick = "";
    this.emptyMessage = null;
    this._build();
  }

  // ----- DOM -------------------------------------------------------------

  _build() {
    this.root.innerHTML = `
      <div class="scroller" tabindex="0" role="grid" aria-label="Assets">
        <div class="thead" role="row">${COLUMNS.map(
          (c, i) => `<div class="th" data-col="${c.id}" data-index="${i}" title="Right-click to open filter menu" role="columnheader">
              <span class="th-label">${c.label}</span><span class="sort-ind"></span>
              <button class="filter-btn" data-filter="${c.id}" title="Filter" aria-label="Filter ${c.label}"></button>
              <span class="resizer" data-resize="${i}"></span></div>`,
        ).join("")}</div>
        <div class="tbody"></div>
      </div>
      <div class="empty-state"></div>`;
    this.scroller = this.root.querySelector(".scroller");
    this.thead = this.root.querySelector(".thead");
    this.tbody = this.root.querySelector(".tbody");
    this.empty = this.root.querySelector(".empty-state");
    this._applyWidths();

    this.scroller.addEventListener("scroll", () => this._scheduleRender(), { passive: true });
    new ResizeObserver(() => this._scheduleRender()).observe(this.scroller);

    // Header: sort on click, filter on right-click / filter button / long-press.
    this.thead.addEventListener("click", (e) => {
      if (e.target.closest(".resizer")) return;
      const fbtn = e.target.closest(".filter-btn");
      const th = e.target.closest(".th");
      if (!th) return;
      if (fbtn) {
        e.stopPropagation();
        this.openFilterMenu(th.dataset.col, th);
        return;
      }
      if (this._suppressHeaderClick) return;
      const col = th.dataset.col;
      this.sort = { col, dir: this.sort.col === col ? -this.sort.dir : 1 };
      this._recompute(true);
    });
    this.thead.addEventListener("contextmenu", (e) => {
      const th = e.target.closest(".th");
      if (!th) return;
      e.preventDefault();
      this.openFilterMenu(th.dataset.col, th);
    });
    this._longPress(this.thead, (target) => {
      const th = target.closest(".th");
      if (th) {
        this._suppressHeaderClick = true;
        setTimeout(() => (this._suppressHeaderClick = false), 400);
        this.openFilterMenu(th.dataset.col, th);
      }
    });

    // A tap must not focus the filter button (focus scrolls the table sideways);
    // it stays in the tab order for keyboard users.
    this.thead.addEventListener("mousedown", (e) => e.target.closest(".filter-btn") && e.preventDefault());

    // Column resizing.
    this.thead.addEventListener("pointerdown", (e) => {
      const handle = e.target.closest(".resizer");
      if (!handle) return;
      e.preventDefault();
      e.stopPropagation();
      const index = Number(handle.dataset.resize);
      const startX = e.clientX;
      const startW = this.widths[index];
      handle.setPointerCapture(e.pointerId);
      const move = (ev) => {
        this.widths[index] = Math.max(36, startW + ev.clientX - startX);
        this._applyWidths();
      };
      const up = () => {
        handle.removeEventListener("pointermove", move);
        handle.removeEventListener("pointerup", up);
        handle.removeEventListener("pointercancel", up);
        this._suppressHeaderClick = true;
        setTimeout(() => (this._suppressHeaderClick = false), 50);
      };
      handle.addEventListener("pointermove", move);
      handle.addEventListener("pointerup", up);
      handle.addEventListener("pointercancel", up);
    });
    this.thead.addEventListener("dblclick", (e) => {
      const handle = e.target.closest(".resizer");
      if (handle) this.autoFit([Number(handle.dataset.resize)], true);
    });

    // Rows.
    this.tbody.addEventListener("click", (e) => {
      const tr = e.target.closest(".tr");
      if (!tr || this._suppressRowClick) return;
      const index = Number(tr.dataset.index);
      if (this.selectMode) this._toggle(index);
      else if (e.shiftKey) this._selectRange(index, e.ctrlKey || e.metaKey);
      else if (e.ctrlKey || e.metaKey) this._toggle(index);
      else {
        this._selectOnly(index);
        if (this.compact || e.pointerType === "touch" || matchMedia("(pointer: coarse)").matches) this.onActivate(this.view[index]);
      }
      this.scroller.focus({ preventScroll: true });
    });
    this.tbody.addEventListener("dblclick", (e) => {
      const tr = e.target.closest(".tr");
      if (tr) this.onActivate(this.view[Number(tr.dataset.index)]);
    });
    this._longPress(this.tbody, (target) => {
      const tr = target.closest(".tr");
      if (!tr) return;
      this._suppressRowClick = true;
      setTimeout(() => (this._suppressRowClick = false), 400);
      const index = Number(tr.dataset.index);
      if (this.compact) {
        this.onLongPress(index);
        return;
      }
      // Table layout on a touch screen: a long-press adds the row (like Ctrl+click),
      // and dragging on from there selects rows like a mouse drag.
      this._toggle(index);
      this._dragStart(index, true);
    });
    this._bindDragSelect();

    this.scroller.addEventListener("keydown", (e) => this._onKey(e));
  }

  // ----- drag selection ----------------------------------------------------
  // Press and drag with the mouse (or long-press then drag on a touch screen)
  // selects the rows from the pressed one to the one under the pointer, like Qt's
  // extended selection. With Ctrl (and after a long-press) earlier picks are kept.

  _bindDragSelect() {
    this.tbody.addEventListener("pointerdown", (e) => {
      if (e.pointerType !== "mouse" || e.button !== 0 || e.shiftKey || this.selectMode) return;
      const tr = e.target.closest(".tr");
      if (!tr) return;
      this._suppressRowClick = false;
      this._dragStart(Number(tr.dataset.index), e.ctrlKey || e.metaKey, true);
      this._drag.mouse = true;
      const move = (ev) => this._dragMove(ev.clientY);
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
        window.removeEventListener("pointercancel", up);
        this._dragEnd();
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      window.addEventListener("pointercancel", up);
    });
    // Touch drags start with a long-press (see above); until then a move scrolls.
    this.tbody.addEventListener(
      "touchmove",
      (e) => {
        if (!this._drag || e.touches.length !== 1) return;
        e.preventDefault();
        this._dragMove(e.touches[0].clientY);
      },
      { passive: false },
    );
    const touchEnd = () => this._drag && this._dragEnd();
    this.tbody.addEventListener("touchend", touchEnd);
    this.tbody.addEventListener("touchcancel", touchEnd);
  }

  _indexAt(clientY) {
    const top = this.tbody.getBoundingClientRect().top;
    return Math.max(0, Math.min(this.view.length - 1, Math.floor((clientY - top) / this.rowHeight)));
  }

  /** pending: nothing changes until the pointer reaches another row (a plain click stays a click). */
  _dragStart(index, additive, pending = false) {
    this._drag = { start: index, last: pending ? index : -1, base: additive ? new Set(this.selected) : new Set(), moved: false, y: 0, raf: 0 };
  }

  _dragMove(clientY) {
    const d = this._drag;
    if (!d) return;
    d.y = clientY;
    this._dragSelect();
    this._dragAutoScroll();
  }

  _dragSelect() {
    const d = this._drag;
    const index = this._indexAt(d.y);
    if (index === d.last) return;
    d.last = index;
    d.moved = true;
    const [lo, hi] = d.start < index ? [d.start, index] : [index, d.start];
    this.selected = new Set(d.base);
    for (let i = lo; i <= hi; i++) this.selected.add(this.view[i].key);
    this.anchor = d.start;
    this.cursor = index;
    this._emit();
  }

  /** Scroll while the pointer is held near the top or bottom edge. */
  _dragAutoScroll() {
    const d = this._drag;
    if (!d || d.raf) return;
    const step = () => {
      d.raf = 0;
      if (this._drag !== d) return;
      const r = this.scroller.getBoundingClientRect();
      const top = r.top + this.headerHeight + 24;
      const bottom = r.bottom - 24;
      const dy = d.y < top ? -Math.min(24, Math.ceil((top - d.y) / 3)) : d.y > bottom ? Math.min(24, Math.ceil((d.y - bottom) / 3)) : 0;
      if (!dy) return;
      this.scroller.scrollTop += dy;
      this._dragSelect();
      d.raf = requestAnimationFrame(step);
    };
    d.raf = requestAnimationFrame(step);
  }

  _dragEnd() {
    const d = this._drag;
    this._drag = null;
    if (!d) return;
    cancelAnimationFrame(d.raf);
    // The click that ends a mouse drag must not reselect the row under the pointer
    // (cleared on the next press). A moved touch fires no click.
    if (d.moved && d.mouse) this._suppressRowClick = true;
  }

  _longPress(el, fn) {
    let timer = null;
    let start = null;
    el.addEventListener("touchstart", (e) => {
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      start = { x: t.clientX, y: t.clientY };
      const target = e.target;
      timer = setTimeout(() => {
        timer = null;
        navigator.vibrate?.(15);
        fn(target);
      }, 550);
    }, { passive: true });
    const cancel = () => {
      clearTimeout(timer);
      timer = null;
    };
    el.addEventListener("touchmove", (e) => {
      const t = e.touches[0];
      if (start && (Math.abs(t.clientX - start.x) > 8 || Math.abs(t.clientY - start.y) > 8)) cancel();
    }, { passive: true });
    el.addEventListener("touchend", cancel);
    el.addEventListener("touchcancel", cancel);
  }

  _applyWidths() {
    this.root.style.setProperty("--cols", this.widths.map((w) => `${w}px`).join(" "));
  }

  _scheduleRender() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = null;
      this.render();
    });
  }

  get headerHeight() {
    return this.compact ? 0 : 24;
  }

  /** Phone layout: one two-line row per asset instead of table columns. */
  setCompact(on) {
    if (this.compact === on) return;
    this.compact = on;
    this.root.classList.toggle("compact", on);
    this.render();
  }

  /** Free-text search over name, container, PathID and source file (phone search box). */
  setQuickSearch(text) {
    const quick = text.trim().toLowerCase();
    if (quick === this.quick) return;
    this.quick = quick;
    this._recompute(false);
  }

  setSort(col, dir) {
    this.sort = { col, dir };
    this._recompute(true);
  }

  activeFilterCount() {
    return COLUMNS.filter((c) => this.hasFilter(c.id)).length + (this.quick ? 1 : 0);
  }

  get rowHeight() {
    return parseFloat(getComputedStyle(this.root).getPropertyValue("--abv-row-h")) || 26;
  }

  render() {
    const rowH = this.rowHeight;
    this.tbody.style.height = `${this.view.length * rowH}px`;
    const viewport = this.scroller.clientHeight - this.headerHeight;
    const first = Math.max(0, Math.floor(this.scroller.scrollTop / rowH) - OVERSCAN);
    const last = Math.min(this.view.length, Math.ceil((this.scroller.scrollTop + viewport) / rowH) + OVERSCAN);
    let html = "";
    for (let i = first; i < last; i++) {
      const a = this.view[i];
      const cls = ["tr"];
      if (i % 2) cls.push("alt");
      if (a.changed) cls.push("changed");
      if (this.selected.has(a.key)) cls.push("selected");
      if (i === this.cursor) cls.push("cursor");
      html += `<div class="${cls.join(" ")}" data-index="${i}" style="top:${i * rowH}px" role="row">`;
      if (this.compact) {
        const name = cellText(a, "name");
        const meta = [a.type, formatByteSize(a.size), a.source || EMPTY_CELL_TEXT].map(escapeHtml).join(" · ");
        const sub = a.container ? escapeHtml(a.container) : `PathID ${escapeHtml(a.path_id)}`;
        html += `<span class="m-check"></span><div class="m-cell"><div class="m-name${a.name ? "" : " is-placeholder"}">${escapeHtml(name)}</div>
          <div class="m-meta">${meta}</div><div class="m-sub">${sub}</div></div>`;
      } else {
        for (const c of COLUMNS) {
          const text = cellText(a, c.id);
          const title = c.id === "size" ? String(a.size) : c.id === "source" ? a.source_name || a.source : c.id === "container" ? a.container : text;
          html += `<div class="td${isPlaceholder(a, c.id) ? " is-placeholder" : ""}" title="${escapeHtml(title)}">${escapeHtml(text)}</div>`;
        }
      }
      html += "</div>";
    }
    this.tbody.innerHTML = html;

    for (const th of this.thead.children) {
      const col = th.dataset.col;
      const ind = th.querySelector(".sort-ind");
      ind.className = `sort-ind${this.sort.col === col ? (this.sort.dir > 0 ? " asc" : " desc") : ""}`;
      th.classList.toggle("filtered", this.hasFilter(col));
    }

    let message = "";
    if (this.emptyMessage) message = this.emptyMessage;
    else if (!this.assets.length) message = "";
    else if (!this.view.length) message = "No assets match the current filters.";
    this.empty.innerHTML = message;
    this.empty.style.display = message ? "flex" : "none";
  }

  setEmptyMessage(html) {
    this.emptyMessage = html;
    this.render();
  }

  // ----- data ------------------------------------------------------------

  /** Replace the asset list. Filter values are refreshed like FilterHeader.set_filter_boxes(). */
  setAssets(assets, { autoFit = false, keepSelection = false } = {}) {
    this.assets = assets;
    if (!keepSelection) {
      this.selected.clear();
      this.anchor = this.cursor = -1;
    } else {
      const keys = new Set(assets.map((a) => a.key));
      for (const k of [...this.selected]) if (!keys.has(k)) this.selected.delete(k);
    }
    for (const c of COLUMNS.filter((c) => c.filter === "set")) {
      const values = [...new Set(assets.map((a) => a[c.id] ?? ""))].sort(collator.compare);
      const previous = this.uniques.get(c.id) || [];
      this.uniques.set(c.id, values);
      const f = this.filters.get(c.id);
      if (!f || (f.kind === "set" && f.values.size === previous.length && previous.every((v) => f.values.has(v)))) {
        this.filters.set(c.id, { kind: "set", values: new Set(values) });
      }
    }
    if (autoFit) this.sort = { col: "name", dir: 1 };
    this._recompute(false);
    if (autoFit) requestAnimationFrame(() => this.autoFit());
    this.onSelectionChange(this.getSelected());
  }

  clear() {
    this.filters.clear();
    this.uniques.clear();
    this.setAssets([]);
  }

  /** Re-render after asset flags (changed) were mutated in place. */
  refresh() {
    this._recompute(false);
  }

  hasFilter(col) {
    const f = this.filters.get(col);
    if (!f) return false;
    if (f.kind === "set") return !(this.uniques.get(col) || []).every((v) => f.values.has(v));
    return Boolean(f.text);
  }

  clearFilters() {
    for (const [col, values] of this.uniques) this.filters.set(col, { kind: "set", values: new Set(values) });
    for (const c of COLUMNS) if (this.filters.get(c.id)?.kind === "text") this.filters.delete(c.id);
    this._recompute(false);
  }

  _matches(a) {
    if (this.quick) {
      const hay = `${a.name}\n${a.container}\n${a.path_id}\n${a.source}\n${a.type}`.toLowerCase();
      if (!hay.includes(this.quick)) return false;
    }
    for (const [col, f] of this.filters) {
      if (f.kind === "set") {
        if (!f.values.has(a[col] ?? "")) return false;
      } else if (f.text) {
        const text = cellText(a, col);
        if (f.matchCase ? !text.includes(f.text) : !text.toLowerCase().includes(f.text.toLowerCase())) return false;
      }
    }
    return true;
  }

  _recompute(keepCursorVisible) {
    const cursorKey = this.view[this.cursor]?.key;
    const { col, dir } = this.sort;
    this.view = this.assets.filter((a) => this._matches(a)).sort((a, b) => dir * compare(a, b, col) || compare(a, b, "path_id"));
    const visible = new Set(this.view.map((a) => a.key));
    // Hidden rows leave the selection. While a filter menu is open the
    // selection is taken from when it opened, so a row hidden by one click and
    // shown again by the next is still selected.
    const base = this._menuSelection ?? this.selected;
    const next = new Set([...base].filter((k) => visible.has(k)));
    const changed = next.size !== this.selected.size || [...next].some((k) => !this.selected.has(k));
    this.selected = next;
    this.cursor = cursorKey ? this.view.findIndex((a) => a.key === cursorKey) : -1;
    this.anchor = this.cursor;
    this.render();
    this.onFiltersChanged();
    if (keepCursorVisible && this.cursor >= 0) this.scrollToIndex(this.cursor);
    if (changed) this.onSelectionChange(this.getSelected());
  }

  // ----- selection -------------------------------------------------------

  getSelected() {
    return this.view.filter((a) => this.selected.has(a.key));
  }

  _emit() {
    this.render();
    this.onSelectionChange(this.getSelected());
  }

  _selectOnly(index) {
    this.selected = new Set(this.view[index] ? [this.view[index].key] : []);
    this.anchor = this.cursor = index;
    this._emit();
  }

  _toggle(index) {
    const key = this.view[index]?.key;
    if (!key) return;
    if (this.selected.has(key)) this.selected.delete(key);
    else this.selected.add(key);
    this.anchor = this.cursor = index;
    this._emit();
  }

  _selectRange(index, additive) {
    const anchor = this.anchor >= 0 ? this.anchor : index;
    if (!additive) this.selected.clear();
    const [lo, hi] = anchor < index ? [anchor, index] : [index, anchor];
    for (let i = lo; i <= hi; i++) this.selected.add(this.view[i].key);
    this.cursor = index;
    this._emit();
  }

  selectAll() {
    this.selected = new Set(this.view.map((a) => a.key));
    this._emit();
  }

  clearSelection() {
    this.selected.clear();
    this._emit();
  }

  selectKey(key) {
    const index = this.view.findIndex((a) => a.key === key);
    if (index >= 0) {
      this._selectOnly(index);
      this.scrollToIndex(index);
    }
  }

  setSelectMode(on) {
    this.selectMode = on;
    this.root.classList.toggle("select-mode", on);
  }

  scrollToIndex(index) {
    const rowH = this.rowHeight;
    const top = index * rowH;
    const viewH = this.scroller.clientHeight - this.headerHeight - 10;
    if (top < this.scroller.scrollTop) this.scroller.scrollTop = top;
    else if (top + rowH > this.scroller.scrollTop + viewH) this.scroller.scrollTop = top + rowH - viewH;
  }

  _onKey(e) {
    if (!this.view.length) return;
    const page = Math.max(1, Math.floor((this.scroller.clientHeight - this.headerHeight) / this.rowHeight) - 1);
    let next = null;
    switch (e.key) {
      case "ArrowDown":
        next = Math.min(this.view.length - 1, this.cursor + 1);
        break;
      case "ArrowUp":
        next = Math.max(0, this.cursor - 1);
        break;
      case "PageDown":
        next = Math.min(this.view.length - 1, this.cursor + page);
        break;
      case "PageUp":
        next = Math.max(0, this.cursor - page);
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = this.view.length - 1;
        break;
      case "Enter":
        if (this.cursor >= 0) this.onActivate(this.view[this.cursor]);
        return;
      case " ":
        if (this.cursor >= 0 && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();
          this._toggle(this.cursor);
        }
        return;
      default:
        return;
    }
    e.preventDefault();
    if (e.shiftKey) this._selectRange(next, false);
    else if (e.ctrlKey || e.metaKey) {
      this.cursor = next;
      this.render();
    } else this._selectOnly(next);
    this.scrollToIndex(next);
  }

  // ----- auto-fit --------------------------------------------------------

  autoFit(columns = COLUMNS.map((_, i) => i), allRows = false) {
    const canvas = (this._canvas ||= document.createElement("canvas"));
    const ctx = canvas.getContext("2d");
    const font = getComputedStyle(this.root).fontFamily;
    const rowH = this.rowHeight;
    const first = Math.floor(this.scroller.scrollTop / rowH);
    const count = allRows ? Math.min(this.view.length, 2000) : Math.ceil((this.scroller.clientHeight - this.headerHeight) / rowH) + 1;
    const rows = allRows ? this.view.slice(0, count) : this.view.slice(first, first + count);
    const headers = this.thead.querySelectorAll(".th");
    for (const i of columns) {
      const c = COLUMNS[i];
      // The header needs room for its label (measured in the real header font)
      // plus padding, sort arrow, filter button and gaps.
      const label = headers[i]?.querySelector(".th-label");
      let width = (label ? label.scrollWidth : c.label.length * 8) + HEADER_CHROME;
      ctx.font = `13px ${font}`;
      for (const a of rows) width = Math.max(width, ctx.measureText(cellText(a, c.id)).width + CELL_TEXT_PADDING);
      this.widths[i] = Math.round(Math.min(Math.max(width, 40), MAX_COLUMN_WIDTH));
    }
    this._applyWidths();
  }

  // ----- filter menu -----------------------------------------------------

  openFilterMenu(col, th) {
    closeFilterMenu();
    this._menuSelection = new Set(this.selected);
    activeTable = this;
    const column = COLUMNS.find((c) => c.id === col);
    const menu = document.createElement("div");
    menu.className = "filter-menu";
    menu.innerHTML = `<div class="fm-title">Filter: ${column.label}</div>`;
    if (column.filter === "set") {
      const values = this.uniques.get(col) || [];
      const current = this.filters.get(col)?.values || new Set(values);
      // Each value is ticked when it is shown; Select All is ticked when all are,
      // half-ticked (indeterminate) when some are, and ticks or clears them all.
      menu.insertAdjacentHTML(
        "beforeend",
        `<label class="fm-item"><input type="checkbox" class="form-check-input" data-all />Select All</label>
         <div class="fm-sep"></div>
         <div class="fm-list">${values
           .map(
             (v, i) => `<label class="fm-item"><input type="checkbox" class="form-check-input" data-i="${i}" ${current.has(v) ? "checked" : ""}/>${escapeHtml(v === "" ? EMPTY_CELL_TEXT : v)}</label>`,
           )
           .join("")}</div>`,
      );
      const all = menu.querySelector("[data-all]");
      const boxes = [...menu.querySelectorAll("[data-i]")];
      const syncAll = () => {
        const n = boxes.filter((b) => b.checked).length;
        all.checked = n === boxes.length;
        all.indeterminate = n > 0 && n < boxes.length;
      };
      const apply = () => {
        this.filters.set(col, { kind: "set", values: new Set(boxes.filter((b) => b.checked).map((b) => values[Number(b.dataset.i)])) });
        this._recompute(false);
      };
      syncAll();
      all.addEventListener("change", () => {
        boxes.forEach((b) => (b.checked = all.checked));
        syncAll();
        apply();
      });
      boxes.forEach((box) =>
        box.addEventListener("change", () => {
          syncAll();
          apply();
        }),
      );
    } else {
      const current = this.filters.get(col) || { kind: "text", text: "", matchCase: false };
      menu.insertAdjacentHTML(
        "beforeend",
        `<div class="fm-text">
           <div class="fm-input mb-2"><input class="form-control" placeholder="Type to filter..." value="${escapeHtml(current.text)}" spellcheck="false" autocomplete="off"/>
             <button class="fm-clear" title="Clear" ${current.text ? "" : "hidden"}>×</button></div>
           <label class="d-flex align-items-center gap-2"><input type="checkbox" class="form-check-input m-0" ${current.matchCase ? "checked" : ""}/>Match Case</label>
         </div>`,
      );
      const input = menu.querySelector("input.form-control");
      const clear = menu.querySelector(".fm-clear");
      const matchCase = menu.querySelector("input[type=checkbox]");
      const update = () => {
        clear.hidden = !input.value;
        this.filters.set(col, { kind: "text", text: input.value, matchCase: matchCase.checked });
        this._recompute(false);
      };
      input.addEventListener("input", update);
      matchCase.addEventListener("change", update);
      clear.addEventListener("click", () => {
        input.value = "";
        update();
        input.focus();
      });
      input.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === "Escape") closeFilterMenu();
      });
      setTimeout(() => input.focus(), 0);
    }
    document.body.appendChild(menu);
    const rect = th.getBoundingClientRect();
    const mw = menu.offsetWidth;
    const mh = menu.offsetHeight;
    menu.style.left = `${Math.max(4, Math.min(rect.left, innerWidth - mw - 4))}px`;
    menu.style.top = `${Math.max(4, Math.min(rect.bottom, innerHeight - mh - 4))}px`;
    activeMenu = menu;
    setTimeout(() => {
      document.addEventListener("pointerdown", outside, true);
      document.addEventListener("keydown", escClose, true);
    }, 0);
  }
}

let activeMenu = null;
/** The table whose filter menu is open (its selection is kept while the menu is, see _recompute). */
let activeTable = null;
function outside(e) {
  if (activeMenu && !activeMenu.contains(e.target)) closeFilterMenu();
}
function escClose(e) {
  if (e.key === "Escape") closeFilterMenu();
}
export function closeFilterMenu() {
  activeMenu?.remove();
  activeMenu = null;
  if (activeTable) activeTable._menuSelection = null;
  activeTable = null;
  document.removeEventListener("pointerdown", outside, true);
  document.removeEventListener("keydown", escClose, true);
}
